<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modalTitle"
    :subtitle="organization.name"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
  >
    <div>
      <v-row>
        <v-col>
          <v-text-field
            v-bind:label="$t('Name')"
            v-model="organization.name"
            :error-messages="nameErrors"
            @input="$v.organization.name.$touch()"
            @blur="$v.organization.name.$touch()"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-text-field
            type="number"
            v-bind:label="`${$t('StorageQuota')} (GB)`"
            v-model="organization.quota"
            :error-messages="quotaErrors"
            @input="$v.organization.quota.$touch()"
            @blur="$v.organization.quota.$touch()"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            type="number"
            v-bind:label="$t('FullAccessUsers')"
            v-model="organization.fullUsersLimit"
            :error-messages="fullUsersLimitErrors"
            @input="$v.organization.fullUsersLimit.$touch()"
            @blur="$v.organization.fullUsersLimit.$touch()"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-text-field
            type="number"
            v-bind:label="$t('ReadonlyUsers')"
            v-model="organization.readonlyUsersLimit"
            :error-messages="readonlyUsersLimitErrors"
            @input="$v.organization.readonlyUsersLimit.$touch()"
            @blur="$v.organization.readonlyUsersLimit.$touch()"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-text-field
            type="number"
            v-bind:label="$t('ConcurrentUsers')"
            v-model="organization.concurrentUsersLimit"
            :error-messages="concurrentUsersLimitErrors"
            @input="$v.organization.concurrentUsersLimit.$touch()"
            @blur="$v.organization.concurrentUsersLimit.$touch()"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            type="number"
            v-bind:label="$t('RegisteredDevices')"
            v-model="organization.registeredDevicesLimit"
            :error-messages="registeredDevicesLimitErrors"
            @input="$v.organization.registeredDevicesLimit.$touch()"
            @blur="$v.organization.registeredDevicesLimit.$touch()"
          ></v-text-field>
        </v-col>
        <v-col>
          <v-text-field
            type="number"
            v-bind:label="$t('TwoFactorAuthenticationUsers')"
            v-model="organization.twoFactorAuthenticationUsersLimit"
            :error-messages="twoFactorAuthenticationUsersLimitErrors"
            @input="$v.organization.twoFactorAuthenticationUsersLimit.$touch()"
            @blur="$v.organization.twoFactorAuthenticationUsersLimit.$touch()"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col v-show="!logoEdit">
          <img
            v-bind:src="logo"
            v-bind:alt="organization.name"
            style="max-width: 160px"
          />
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" color="primary" icon @click="editLogo(true)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Edit") }}</span>
          </v-tooltip>
        </v-col>
        <v-col v-show="logoEdit">
          <vue-dropzone
            id="dropZone"
            :ref="dropZoneKey"
            :options="dropzoneOptions"
            @vdropzone-success="onFileSuccess"
            @vdropzone-removed-file="onFileRemoved"
          ></vue-dropzone>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" color="error" icon @click="editLogo(false)">
                <v-icon>mdi-cancel</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Cancel") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
  </base-modal>
</template>
<script>
import { required, minValue } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import OrganizationService from "@/services/OrganizationService";
import Organization from "@/ecm/models/Organization";
import Attachment from "@/ecm/models/document/Attachment";
import Utils from "@/ecm/common/Utils";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";

export default {
  name: "OrganizationModal",
  components: {
    vueDropzone: vue2Dropzone,
  },
  created() {
    this.$v.$reset();
  },
  data() {
    return {
      modal: {
        title: this.$t("Organization"),
        key: "organizationModal",
      },
      open: false,
      loading: false,
      organization: new Organization(),
      logoEdit: false,
      dropZoneKey: "drop-zone-org-logo",
      dropzoneOptions: {
        url: "",
        thumbnailWidth: 150,
        timeout: 30000, //30 seconds
        maxFilesize: 10, //10 MB
        destroyDropzone: true,
        headers: {
          Authorization: "bearer " + this.$store.getters["account/token"],
        },
        addRemoveLinks: true,
        acceptedFiles: "image/*",
        dictDefaultMessage: this.$t("DropzoneMessage"),
        dictRemoveFile: this.$t("Remove"),
        dictInvalidFileType: this.$t("DropzoneInvalidFileType"),
      },
      isNew: true,
    };
  },
  mixins: [validationMixin],
  validations: {
    organization: {
      name: { required },
      quota: {
        required,
        minValue: minValue(1),
      },
      fullUsersLimit: {
        required,
        minValue: minValue(0),
      },
      readonlyUsersLimit: {
        required,
        minValue: minValue(0),
      },
      concurrentUsersLimit: {
        required,
        minValue: minValue(1),
      },
      registeredDevicesLimit: {
        required,
        minValue: minValue(0),
      },
      twoFactorAuthenticationUsersLimit: {
        required,
        minValue: minValue(0),
      },
    },
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modal.key];
    },
    modalTitle() {
      return (
        this.modal.title +
        (this.organization.name ? " - " + this.organization.name : "")
      );
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.organization.name.$dirty) return errors;
      !this.$v.organization.name.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    quotaErrors() {
      const errors = [];
      if (!this.$v.organization.quota.$dirty) return errors;
      !this.$v.organization.quota.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.organization.quota.minValue &&
        errors.push(`${this.$t("MinValue")}: 1`);
      return errors;
    },
    fullUsersLimitErrors() {
      const errors = [];
      if (!this.$v.organization.fullUsersLimit.$dirty) return errors;
      !this.$v.organization.fullUsersLimit.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.organization.fullUsersLimit.minValue &&
        errors.push(`${this.$t("MinValue")}: 0`);
      return errors;
    },
    readonlyUsersLimitErrors() {
      const errors = [];
      if (!this.$v.organization.readonlyUsersLimit.$dirty) return errors;
      !this.$v.organization.readonlyUsersLimit.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.organization.readonlyUsersLimit.minValue &&
        errors.push(`${this.$t("MinValue")}: 0`);
      return errors;
    },
    concurrentUsersLimitErrors() {
      const errors = [];
      if (!this.$v.organization.concurrentUsersLimit.$dirty) return errors;
      !this.$v.organization.concurrentUsersLimit.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.organization.concurrentUsersLimit.minValue &&
        errors.push(`${this.$t("MinValue")}: 1`);
      return errors;
    },
    registeredDevicesLimitErrors() {
      const errors = [];
      if (!this.$v.organization.registeredDevicesLimit.$dirty) return errors;
      !this.$v.organization.registeredDevicesLimit.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.organization.registeredDevicesLimit.minValue &&
        errors.push(`${this.$t("MinValue")}: 0`);
      return errors;
    },
    twoFactorAuthenticationUsersLimitErrors() {
      const errors = [];
      if (!this.$v.organization.twoFactorAuthenticationUsersLimit.$dirty)
        return errors;
      !this.$v.organization.twoFactorAuthenticationUsersLimit.required &&
        errors.push(
          `${this.$t("RequiredField")} (${this.$t("FieldTypeNumeric")})`
        );
      !this.$v.organization.twoFactorAuthenticationUsersLimit.minValue &&
        errors.push(`${this.$t("MinValue")}: 0`);
      return errors;
    },
    dropZone() {
      return this.$refs[this.dropZoneKey];
    },
    logo() {
      return `${this.$store.getters["account/apiUrl"]}/api/organization/logo/${this.organization.id}?access_token=${this.$store.getters["account/token"]}`;
    },
  },
  methods: {
    saveItem() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      if (this.organization.logo == null) {
        Utils.toast.error(this.$t("LogoRequired"));
      }

      this.modalInstance.progress(true);

      OrganizationService.save(this.organization.toDto())
        .then((result) => {
          if (this.isNew) {
            Utils.toast.success(
              this.$t("Organization") + " " + this.$t("CreatedSuccessfullyFem")
            );
          } else {
            Utils.toast.success(
              this.$t("Organization") + " " + this.$t("UpdatedSuccessfullyFem")
            );
          }
          this.hide();
          this.$emit("on-save");
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    edit(organizationId) {
      this.$v.$reset();
      this.isNew = false;
      this.show();
      const url = `${this.$store.getters["account/apiUrl"]}/api/organization/upload-logo/${organizationId}`;

      this.dropzoneOptions.url = url;
      this.loading = true;

      OrganizationService.get(organizationId).then((result) => {
        this.loading = false;
        this.organization = new Organization(result);
      });
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
      this.isNew = true;
    },
    onFileSuccess(file, response) {
      var attachment = new Attachment();
      attachment.fileId = response;
      attachment.fileName = file.name;
      this.organization.logo = attachment;
    },
    onFileRemoved(file) {
      this.organization.logo = null;
    },
    editLogo(edit) {
      this.logoEdit = edit;
    },
  },
};
</script>
